import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_CONFIG } from '../aws-config';

type ViewState = 'loading' | 'success' | 'already_viewed' | 'expired' | 'not_found' | 'error';

const OtpViewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [secret, setSecret] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setViewState('not_found');
      return;
    }

    fetch(`${API_CONFIG.baseURL}/public/otp/${token}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(async res => {
        const data = await res.json();
        if (res.ok && data.success) {
          setSecret(data.secret);
          setViewState('success');
        } else if (res.status === 410) {
          setViewState(data.reason === 'expired' ? 'expired' : 'already_viewed');
        } else if (res.status === 404) {
          setViewState('not_found');
        } else {
          setViewState('error');
        }
      })
      .catch(() => setViewState('error'));
  }, [token]);

  const handleCopy = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'Arial, sans-serif'
  };

  const cardStyle: React.CSSProperties = {
    background: 'white',
    borderRadius: '12px',
    padding: '2.5rem',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center'
  };

  const headerStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '1rem 2rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    fontSize: '1.2rem',
    fontWeight: 700,
    letterSpacing: '0.5px'
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>Data Crate — Shared Secret</div>

        {viewState === 'loading' && (
          <div style={{ color: '#666', fontSize: '15px' }}>Retrieving your secret…</div>
        )}

        {viewState === 'success' && (
          <>
            <p style={{ color: '#333', fontSize: '14px', marginBottom: '1.25rem' }}>
              One Time Secret. This is the only time it will be shown — please copy it to store safely.
            </p>
            <div style={{
              background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: '8px',
              padding: '1.25rem', marginBottom: '1rem', position: 'relative'
            }}>
              <div style={{
                fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 700,
                color: '#1a1a1a', letterSpacing: '2px', wordBreak: 'break-all'
              }}>
                {secret}
              </div>
            </div>
            <button
              onClick={handleCopy}
              style={{
                width: '100%', padding: '0.75rem',
                background: copied ? '#4caf50' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white', border: 'none', borderRadius: '6px',
                fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                transition: 'background 0.2s', marginBottom: '1rem'
              }}
            >
              {copied ? '✓ Copied to clipboard!' : 'Copy Secret'}
            </button>
            <div style={{
              fontSize: '12px', color: '#e57373',
              background: '#fff8f8', border: '1px solid #ffcdd2',
              borderRadius: '6px', padding: '0.75rem'
            }}>
              This secret has been permanently deleted and cannot be retrieved again.
            </div>
          </>
        )}

        {viewState === 'already_viewed' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
            <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>Secret Already Viewed</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              This secret has already been viewed and permanently deleted. One-time secrets can only be accessed once.
            </p>
          </>
        )}

        {viewState === 'expired' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏰</div>
            <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>Secret Expired</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              This secret has expired and is no longer available. Please ask the sender to create a new one.
            </p>
          </>
        )}

        {viewState === 'not_found' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❓</div>
            <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>Invalid Link</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              This secret link is invalid or does not exist. Please check the link and try again.
            </p>
          </>
        )}

        {viewState === 'error' && (
          <>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h3 style={{ color: '#333', marginBottom: '0.5rem' }}>Something Went Wrong</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>
              We were unable to retrieve this secret. Please try again or contact support.
            </p>
          </>
        )}

        <div style={{ marginTop: '2rem', fontSize: '12px', color: '#bbb' }}>
          Powered by{' '}
          <a href="https://app-dev.datacrate.now" target="_blank" rel="noopener noreferrer"
            style={{ color: '#667eea', fontWeight: 700, textDecoration: 'none' }}>
            Data Crate
          </a>
          {' '}· Secure file and secret sharing
        </div>
      </div>
    </div>
  );
};

export default OtpViewPage;
